import React from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import './Medcall2MessageForm.css';

const EMAIL_REGEX = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

// The same CloseOutlined / CloseCircleFilled silhouettes used by the legacy Select.
const MessageCloseIcon = ({ circle = false }: { circle?: boolean }) => (
  <svg aria-hidden="true" fill="currentColor" fillRule="evenodd" focusable="false" viewBox="64 64 896 896">
    <path d={circle
      ? 'M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm127.98 274.82h-.04l-.08.06L512 466.75 384.14 338.88c-.04-.05-.06-.06-.08-.06a.12.12 0 00-.07 0c-.03 0-.05.01-.09.05l-45.02 45.02a.2.2 0 00-.05.09.12.12 0 000 .07v.02a.27.27 0 00.06.06L466.75 512 338.88 639.86c-.05.04-.06.06-.06.08a.12.12 0 000 .07c0 .03.01.05.05.09l45.02 45.02a.2.2 0 00.09.05.12.12 0 00.07 0c.02 0 .04-.01.08-.05L512 557.25l127.86 127.87c.04.04.06.05.08.05a.12.12 0 00.07 0c.03 0 .05-.01.09-.05l45.02-45.02a.2.2 0 00.05-.09.12.12 0 000-.07v-.02a.27.27 0 00-.05-.06L557.25 512l127.87-127.86c.04-.04.05-.06.05-.08a.12.12 0 000-.07c0-.03-.01-.05-.05-.09l-45.02-45.02a.2.2 0 00-.09-.05.12.12 0 00-.07 0z'
      : 'M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z'} />
  </svg>
);

const RecipientTags = ({ id, label, value, onChange, required = false, missing = false }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  missing?: boolean;
}) => {
  const [entry, setEntry] = React.useState('');
  const [invalid, setInvalid] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const recipients = value.split(/[,\n;]+/).map((item) => item.trim()).filter(Boolean);
  const changeRecipients = (next: string[]) => {
    setInvalid(false);
    onChange(next.join(', '));
  };

  const commitEntry = () => {
    const address = entry.trim();
    if (!address) return;
    if (!EMAIL_REGEX.test(address)) {
      setInvalid(true);
      setEntry('');
      return;
    }
    setInvalid(false);
    setEntry('');
    if (!recipients.includes(address)) onChange([...recipients, address].join(', '));
  };

  return (
    <>
    <div className="medcall2-message-field">
      <label className={required ? 'is-required' : undefined} htmlFor={id}>{label}</label>
      <div className={`medcall2-recipient-tags${required ? ' is-clearable' : ''}${missing ? ' has-error' : ''}`} onClick={() => inputRef.current?.focus()}>
        {recipients.map((address, index) => (
          <span className="medcall2-recipient-tag" key={address}>
            <span>{address}</span>
            <button
              aria-label={`Remove ${address}`}
              id={`${id}-remove-${index}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                changeRecipients(recipients.filter((item) => item !== address));
              }}
              type="button"
            ><MessageCloseIcon /></button>
          </span>
        ))}
        <input
          autoComplete="off"
          id={id}
          onBlur={commitEntry}
          onChange={(event) => setEntry(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitEntry();
              return;
            }
            if (event.key === 'Backspace' && !entry) changeRecipients(recipients.slice(0, -1));
          }}
          placeholder={recipients.length === 0 ? 'email recipients' : ''}
          ref={inputRef}
          style={{ width: entry ? `${Math.max(4, entry.length + 1)}ch` : '4px' }}
          value={entry}
        />
        {required && recipients.length > 0 ? (
          <button
            aria-label="Clear To recipients"
            className="medcall2-recipient-tags__clear"
            id={`${id}-clear`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { setEntry(''); changeRecipients([]); }}
            type="button"
          ><MessageCloseIcon circle /></button>
        ) : null}
      </div>
      {missing ? <div className="medcall2-message-required-error" role="alert">Please add at least one email address.</div> : null}
    </div>
      {invalid ? (
        <div className="medcall2-message-error" role="alert">
          <MessageCloseIcon circle />
          <span>Error. The email format is not correct</span>
          <button aria-label="Dismiss email error" id={`${id}-dismiss-error`} onClick={() => setInvalid(false)} type="button"><MessageCloseIcon /></button>
        </div>
      ) : null}
    </>
  );
};

export type Medcall2MessagePreviewItem = { label: string; value: string };

export const Medcall2MessageForm = ({
  ccRecipients, error, loading, notes, onCancel, onCcChange, onNotesChange,
  onSubmit, onToChange, onUrgentChange, participantName, previewRows,
  sendDisabled = false, sending, toRecipients, toRequired, urgent,
}: {
  ccRecipients: string;
  error?: string;
  loading: boolean;
  notes: string;
  onCancel: () => void;
  onCcChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onToChange: (value: string) => void;
  onUrgentChange: (value: boolean) => void;
  participantName: string;
  previewRows: Medcall2MessagePreviewItem[][];
  sendDisabled?: boolean;
  sending: boolean;
  toRecipients: string;
  toRequired: boolean;
  urgent: boolean;
}) => (
  <form className="medcall2-message-form" onSubmit={onSubmit}>
    {loading ? <div role="status">Loading message defaults...</div> : (
      <>
        <RecipientTags id="medcall-message-to" label="To:" missing={toRequired} onChange={onToChange} required value={toRecipients} />
        <RecipientTags id="medcall-message-cc" label="CC:" onChange={onCcChange} value={ccRecipients} />
      </>
    )}
    {error ? <div className="medcall2-message-error" role="alert">{error}</div> : null}
    <label className="medcall2-message-urgent" htmlFor="medcall-message-urgent">
      <span className="medcall2-message-urgent__control">
        <input checked={urgent} id="medcall-message-urgent" onChange={(event) => onUrgentChange(event.target.checked)} type="checkbox" />
        <span aria-hidden="true" className="medcall2-message-urgent__box" />
      </span>
      <span>Urgent</span>
    </label>
    <div className="medcall2-message-field medcall2-message-body">
      <div className="medcall2-message-field__label">Message Body</div>
      <div className="medcall2-message-preview">
        <p className="medcall2-message-preview__intro">This is regarding {participantName}</p>
        {previewRows.map((row, rowIndex) => (
          <div className={`medcall2-message-preview__row medcall2-message-preview__row--${rowIndex}`} key={rowIndex}>
            {row.map((item) => (
              <p key={item.label}>
                <strong>{item.label}</strong>
                <span>{item.value}</span>
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
    <div className="medcall2-message-field">
      <label htmlFor="medcall-message-notes">Notes</label>
      <Textarea id="medcall-message-notes" onChange={(event) => onNotesChange(event.target.value)} rows={6} value={notes} />
    </div>
    <div className="medcall2-message-footer">
      <Button disabled={sending} id="medcall-message-cancel" onClick={onCancel} type="button" variant="outline">Cancel</Button>
      <Button disabled={loading || sending || sendDisabled} id="medcall-message-send" type="submit">Send Message</Button>
    </div>
  </form>
);
